import React from 'react';
import { PropTypes } from 'prop-types';
// styles
import styled, { css } from 'styled-components';
import { media } from 'utils/style';
// components
import useClickOutside from 'refactored/ui/utils/useClickOutside';
import { withTranslation } from 'react-i18next';
import useAcceptQuoteModal from '../../components/modals/useAcceptQuoteModal';
import useRejectQuoteModal from '../../components/modals/useRejectQuoteModal';
import useCancelTicketModal from '../../components/modals/useCancelTicketModal';
import { STATES } from '../../constants';

const InternalLink = ({ children, onClick, className }) => (
  <div onClick={onClick} className={className}>
    {children}
  </div>
);
const StyledInternalLink = styled(InternalLink)`
  display: block;
  width: 220px;
  padding: 8px 24px;
  font-size: 16px;
  line-height: 24px;
  text-decoration: none;
  color: var(--grey900);
  cursor: pointer;
  svg {
    height: 16px;
    width: 16px;
    margin-right: 5px;
  }
  &:hover {
    background-color: var(--primary100);
  }

  ${({ disabled }) =>
    disabled &&
    css`
      color: var(--grey500);
      cursor: auto;
      &:hover {
        background-color: #fff;
      }
    `}
`;

const Entry = ({ children, role = 'menuitem' }) => (
  <li role={role}>{children}</li>
);

const RowOptionsEntries = ({ ticket, onClose, className, t }) => {
  const clickRef = React.useRef();

  useClickOutside(clickRef, () => {
    onClose();
  });
  const ticketState = ticket.synthetic_state;

  const hasQuote = !!ticket.quote;
  const statesWithInvalidTickets = [
    STATES.CANCELLED,
    STATES.EXPIRED,
    STATES.OUT_OF_SCOPE,
    STATES.ACCEPTED,
    STATES.DONE,
  ];
  const statesWithInvalidQuotes = [
    ...statesWithInvalidTickets,
    STATES.REJECTED,
  ];
  const isQuoteEntryDisabled =
    !hasQuote || statesWithInvalidQuotes.includes(ticketState);
  const isTicketEntryDisabled = statesWithInvalidTickets.includes(ticketState);

  const { openAcceptQuoteModal } = useAcceptQuoteModal(ticket, ticket.quote);
  const { openRejectQuoteModal } = useRejectQuoteModal(ticket, ticket.quote);
  const { openModalCancelTicket } = useCancelTicketModal(ticket);

  return (
    <ul className={className} ref={clickRef}>
      {/* Accept quote*/}
      <Entry>
        <StyledInternalLink
          disabled={isQuoteEntryDisabled}
          onClick={() => {
            if (!isQuoteEntryDisabled) {
              onClose();
              openAcceptQuoteModal();
            }
          }}
        >
          {t('accept_quote')}
        </StyledInternalLink>
      </Entry>
      <Entry role="presentation">
        <hr />
      </Entry>
      {/* Reject quote*/}
      <Entry>
        <StyledInternalLink
          disabled={isQuoteEntryDisabled}
          onClick={() => {
            if (!isQuoteEntryDisabled) {
              onClose();
              openRejectQuoteModal();
            }
          }}
        >
          {t('reject_quote')}
        </StyledInternalLink>
      </Entry>
      <Entry role="presentation">
        <hr />
      </Entry>
      {/* Reject ticket*/}
      <Entry>
        <StyledInternalLink
          disabled={isTicketEntryDisabled}
          onClick={() => {
            if (!isTicketEntryDisabled) {
              onClose();
              openModalCancelTicket();
            }
          }}
        >
          {t('reject_ticket')}
        </StyledInternalLink>
      </Entry>
    </ul>
  );
};

RowOptionsEntries.propTypes = {
  onEntryClick: PropTypes.func,
  onClickOutside: PropTypes.func,
  className: PropTypes.string,
};

const regularCSS = css`
  margin: 0;
  padding: 0;
  list-style: none;
  background-color: var(--white);
  li {
    hr {
      margin: 8px 24px;
    }
  }
`;

const mobileCSS = css`
  li {
    div {
      width: 100%;
    }
  }
`;

const StyledRowOptionsEntries = styled(RowOptionsEntries)`
  ${regularCSS}
  ${media.lessThan('small')`${mobileCSS}`}
`;

export default withTranslation('Emergencies')(StyledRowOptionsEntries);
